import { useCallback, useState, useEffect, useMemo } from "react";
import { Flex, Text, Button, Box, SegmentedControl, Badge } from "@radix-ui/themes";
import { Dialog } from "@/components/shared/dialog";
import Select from "@/components/shared/select";
import {
  ReleaseAccessStatus,
  ReleaseAccessResourceTopic,
  accessStatusToLabel,
  accessStatusOptions,
  STAGED_TOOLTIP_TEXT,
} from "@/v2/entities/ReleaseResourceAccess/ReleaseResourceAccess.model";
import styles from "./ManageAccessDialog.module.scss";
import { CheckCircledIcon, CrossCircledIcon } from "@radix-ui/react-icons";
import { SystemUpdate01Icon } from "@/v2/assets/icons/SystemUpdate01";
import { Tooltip } from "@/components/shared/tooltip";

export interface Topic {
  id: number;
  topic: string;
}

type TopicAccessStatus = "FULL_ACCESS" | "NO_ACCESS";

interface ManageAccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceName: string;
  resourceCount?: number;
  currentStatus?: ReleaseAccessStatus;
  currentTopicsAccess?: ReleaseAccessResourceTopic[];
  publishedTopicsAccess?: ReleaseAccessResourceTopic[];
  currentDefaultTopicAccess?: ReleaseAccessStatus | null;
  topics?: Topic[];
  onUpdateAccess: (
    status: ReleaseAccessStatus,
    topicsAccess?: ReleaseAccessResourceTopic[],
    defaultTopicAccess?: ReleaseAccessStatus | null
  ) => void;
  isLoading?: boolean;
}

export function ManageAccessDialog({
  open,
  onOpenChange,
  resourceName,
  resourceCount,
  currentStatus,
  currentTopicsAccess = [],
  publishedTopicsAccess = [],
  currentDefaultTopicAccess,
  topics = [],
  onUpdateAccess,
  isLoading = false,
}: ManageAccessDialogProps) {
  const [selectedStatus, setSelectedStatus] = useState<ReleaseAccessStatus>(
    currentStatus ?? "FULL_ACCESS"
  );
  const [topicsAccessState, setTopicsAccessState] = useState<Map<string, TopicAccessStatus>>(
    new Map()
  );
  const [defaultTopicAccessStatus, setDefaultTopicAccessStatus] = useState<TopicAccessStatus>(
    "FULL_ACCESS"
  );

  useEffect(() => {
    if (open) {
      if (currentStatus) {
        setSelectedStatus(currentStatus);
      }
      const newMap = new Map<string, TopicAccessStatus>();
      for (const ta of currentTopicsAccess) {
        if (ta.status === "FULL_ACCESS" || ta.status === "NO_ACCESS") {
          newMap.set(ta.topicId, ta.status);
        }
      }
      setTopicsAccessState(newMap);

      if (currentDefaultTopicAccess === "FULL_ACCESS" || currentDefaultTopicAccess === "NO_ACCESS") {
        setDefaultTopicAccessStatus(currentDefaultTopicAccess);
      } else {
        setDefaultTopicAccessStatus("FULL_ACCESS");
      }
    }
  }, [open, currentStatus, currentTopicsAccess, currentDefaultTopicAccess]);

  const getTopicAccessStatus = useCallback(
    (topicId: string): TopicAccessStatus => {
      return topicsAccessState.get(topicId) ?? "FULL_ACCESS";
    },
    [topicsAccessState]
  );

  const getPublishedTopicAccessStatus = useCallback(
    (topicId: string): TopicAccessStatus | null => {
      const published = publishedTopicsAccess.find((ta) => ta.topicId === topicId);
      if (published?.status === "FULL_ACCESS" || published?.status === "NO_ACCESS") {
        return published.status;
      }
      return null;
    },
    [publishedTopicsAccess]
  );

  const isTopicStaged = useCallback(
    (topicId: string): boolean => {
      const current = getTopicAccessStatus(topicId);
      const published = getPublishedTopicAccessStatus(topicId);
      if (published === null) return false;
      return current !== published;
    },
    [getTopicAccessStatus, getPublishedTopicAccessStatus]
  );

  const handleTopicAccessChange = useCallback((topicId: string, status: TopicAccessStatus) => {
    setTopicsAccessState((prev) => {
      const next = new Map(prev);
      next.set(topicId, status);
      return next;
    });
  }, []);

  const buildTopicsAccess = useCallback((): ReleaseAccessResourceTopic[] => {
    return topics.map((topic) => ({
      topicId: String(topic.id),
      status: getTopicAccessStatus(String(topic.id)),
    }));
  }, [topics, getTopicAccessStatus]);

  const handleUpdateAccess = useCallback(() => {
    if (selectedStatus === "LIMITED_ACCESS") {
      onUpdateAccess(selectedStatus, buildTopicsAccess(), defaultTopicAccessStatus);
    } else {
      onUpdateAccess(selectedStatus);
    }
  }, [onUpdateAccess, selectedStatus, buildTopicsAccess, defaultTopicAccessStatus]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const selectOptions = useMemo(
    () =>
      accessStatusOptions.map((status) => ({
        label: accessStatusToLabel[status],
        value: status,
      })),
    []
  );

  const isBulk = resourceCount !== undefined && resourceCount > 1;
  const title = isBulk
    ? `Manage access for ${resourceCount} resources`
    : `Manage access for "${resourceName}"`;
  const subtitle = "Select how this snippet should be used.";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size={selectedStatus === "LIMITED_ACCESS" ? "large" : "medium"} className={styles.dialogContent}>
        <Dialog.Close className={styles.closeButton} />
        <Flex direction="column" gap="4" p="5">
          <Flex direction="column" gap="1">
            <Text size="4" weight="medium" className={styles.title}>
              {title}
            </Text>
            <Text size="3" color="gray" className={styles.subtitle}>
              {subtitle}
            </Text>
          </Flex>

          <Flex direction="column" gap="2">
            <label className={styles.label}>
              <Text size="2">Access level</Text>
            </label>
            <Select
              options={selectOptions}
              value={selectedStatus}
              onChange={(value) =>
                setSelectedStatus(value as ReleaseAccessStatus)
              }
              placeholder="Select access level"
            />
          </Flex>

          {selectedStatus === "LIMITED_ACCESS" && (
            <Flex direction="column" gap="1" p="0px 30px" className={styles.topicsList}>
              <Flex align="center" justify="between" className={styles.defaultAccessSection}>
                <Flex align="start" gap="1" direction="column">
                  <Text size="1" weight="bold" color="gray" highContrast className="flex items-center gap-1">
                    <SystemUpdate01Icon size={12} fill="gray" />
                    Default access for new topics
                  </Text>
                  <Text size="1" color="gray">
                    Sets the access level automatically applied to newly created topics.
                  </Text>
                </Flex>
                <SegmentedControl.Root
                  value={defaultTopicAccessStatus}
                  onValueChange={(value) =>
                    setDefaultTopicAccessStatus(value as TopicAccessStatus)
                  }
                  size="1"
                >
                  <SegmentedControl.Item value="FULL_ACCESS" className={styles.segmentItem}>
                    <Text size="1" className="flex items-center gap-1">
                      <CheckCircledIcon width={12} height={12} />
                      Full Access
                    </Text>
                  </SegmentedControl.Item>
                  <SegmentedControl.Item value="NO_ACCESS" className={styles.segmentItem}>
                    <Text size="1" className="flex items-center gap-1">
                      <CrossCircledIcon width={12} height={12} />
                      No Access
                    </Text>
                  </SegmentedControl.Item>
                </SegmentedControl.Root>
              </Flex>

              {topics.sort((a, b) => a.topic.localeCompare(b.topic)).map((topic) => {
                const topicStatus = getTopicAccessStatus(String(topic.id));
                const staged = isTopicStaged(String(topic.id));
                return (
                  <Flex
                    key={topic.id}
                    align="center"
                    justify="between"
                    className={styles.topicRow}
                  >
                    <Flex align="center" gap="1">
                      <Text size="1" color="gray" highContrast>
                        {topic.topic}
                      </Text>
                      {staged && (
                        <Tooltip>
                          <Tooltip.Trigger>
                            <Badge color="blue" size="1" variant="soft">
                              <Text size="1">staged</Text>
                            </Badge>
                          </Tooltip.Trigger>
                          <Tooltip.Content>
                            <Text size="1">{STAGED_TOOLTIP_TEXT}</Text>
                          </Tooltip.Content>
                        </Tooltip>
                      )}
                    </Flex>
                    <SegmentedControl.Root
                      value={topicStatus}
                      onValueChange={(value) =>
                        handleTopicAccessChange(String(topic.id), value as TopicAccessStatus)
                      }
                      size="1"
                    >
                      <SegmentedControl.Item value="FULL_ACCESS" className={styles.segmentItem}>
                        <Text size="1" className="flex items-center gap-1">
                          <CheckCircledIcon width={12} height={12} />
                          Full Access
                        </Text>
                      </SegmentedControl.Item>
                      <SegmentedControl.Item value="NO_ACCESS" className={styles.segmentItem}>
                        <Text size="1" className="flex items-center gap-1">
                          <CrossCircledIcon width={12} height={12} />
                          No Access
                        </Text>
                      </SegmentedControl.Item>
                    </SegmentedControl.Root>
                  </Flex>
                );
              })}
            </Flex>
          )}

          <Box p="2" px="3" className={styles.infoMessage}>
            <Text size="2" weight="medium" className={styles.infoText}>
              Access level determines how the AI agent can use this resource in responses.
              Changing access affects training immediately and is applied to the agent only on release.
            </Text>
          </Box>

          <Flex gap="3" justify="end">
            <Button
              variant="ghost"
              size="2"
              onClick={handleCancel}
              disabled={isLoading}
              className={styles.cancelButton}
            >
              <Text size="3" color="gray">
                Cancel
              </Text>
            </Button>
            <Button
              variant="solid"
              color="gray"
              size="2"
              highContrast
              onClick={handleUpdateAccess}
              disabled={isLoading}
              className={styles.updateButton}
            >
              {isLoading ? "Updating..." : "Update Access"}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
}
